/**
 * Lazily loads the Google Maps JavaScript API (with the Places library) exactly
 * once and resolves with the `google.maps.places` library namespace.
 *
 * Uses the modern async bootstrap (`loading=async` + `importLibrary`) and the
 * Places API (New) surface, matching the referrer-restricted frontend key
 * provisioned by terraform/google-maps (Maps JS API + Places API New only).
 *
 * Returns a rejected promise if the key is missing or the script fails to load,
 * so callers can degrade gracefully to a plain text input.
 */

import { googleMapsApiKey } from '../config/googleMaps';

let loadPromise = null;

// Trusted Types support: some environments (enterprise-managed Chrome, certain
// security extensions) enforce `require-trusted-types-for 'script'`, which
// forbids assigning a plain string to `script.src`. When the API is present we
// register a pass-through policy so the Maps loader keeps working; otherwise we
// fall back to the plain string. `undefined` marks "TT present but policy
// creation was blocked by CSP", so we don't retry it.
let scriptUrlPolicy = null;

const getScriptUrlPolicy = () => {
  if (scriptUrlPolicy !== null) return scriptUrlPolicy;

  if (window.trustedTypes && window.trustedTypes.createPolicy) {
    try {
      scriptUrlPolicy = window.trustedTypes.createPolicy('zenrent-gmaps-loader', {
        createScriptURL: (url) => url,
      });
    } catch (err) {
      // CSP `trusted-types` directive disallowed creating this policy.
      console.error('Google Maps loader: Trusted Types policy creation blocked', err);
      scriptUrlPolicy = undefined;
    }
  } else {
    scriptUrlPolicy = undefined;
  }

  return scriptUrlPolicy;
};

const toScriptSrc = (url) => {
  const policy = getScriptUrlPolicy();
  return policy ? policy.createScriptURL(url) : url;
};

const injectScript = () =>
  new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve(window.google);
      return;
    }

    const existing = document.getElementById('google-maps-js');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google));
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Google Maps JS API'))
      );
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-maps-js';
    script.async = true;
    script.defer = true;
    const url =
      'https://maps.googleapis.com/maps/api/js' +
      `?key=${encodeURIComponent(googleMapsApiKey)}` +
      '&libraries=places&v=weekly&loading=async';
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error('Failed to load Google Maps JS API'));
    // toScriptSrc returns a TrustedScriptURL when Trusted Types is enforced.
    script.src = toScriptSrc(url);
    document.head.appendChild(script);
  });

/**
 * @returns {Promise<google.maps.PlacesLibrary>} the Places library namespace.
 */
export function loadPlacesLibrary() {
  if (loadPromise) return loadPromise;

  if (!googleMapsApiKey) {
    return Promise.reject(new Error('REACT_APP_GOOGLE_MAPS_API_KEY is not set'));
  }

  loadPromise = injectScript()
    .then((google) => google.maps.importLibrary('places'))
    .catch((err) => {
      console.error('Google Maps JS API failed to load', err);
      // Reset so a later attempt can retry after a transient failure.
      loadPromise = null;
      throw err;
    });

  return loadPromise;
}
